<template>
  <section
    class="native-player"
    :class="{
      'is-preview': isPreview,
      'is-landscape': isLandscape
    }"
  >
    <header v-if="showControls" class="player-control-header">
      <button
        type="button"
        class="function-bar-toggle"
        :class="{ active: functionBarVisible }"
        @click="toggleFunctionBar"
      >
        功能条
      </button>

      <span class="delay-chip" :class="delayClass">
        {{ delayText }}
      </span>

      <el-dropdown trigger="click" @command="selectQuality">
        <button type="button" class="quality-trigger">
          {{ activeProfile.label }}
          <span aria-hidden="true">▾</span>
        </button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item
              v-for="profile in qualityProfiles"
              :key="profile.key"
              :command="profile.key"
              :disabled="profile.key === selectedQuality"
            >
              {{ profile.label }} {{ profile.bitrateKbps }}K · {{ profile.maxFps }} FPS
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </header>

    <div
      ref="playerStage"
      class="player-stage"
      :class="{ 'is-expanded': isPresentationExpanded }"
    >
      <div
        :id="playerElementId"
        ref="playerElement"
        class="player-surface"
        :class="{ 'has-function-bar': showControls && functionBarVisible }"
      ></div>

      <aside v-if="showControls && functionBarVisible" class="player-function-bar" aria-label="云手机功能条">
        <el-tooltip content="隐藏功能条" placement="left" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle class="tool-button" @click="toggleFunctionBar">
            <el-icon><ArrowRight /></el-icon>
          </el-button>
        </el-tooltip>
        <el-tooltip content="云机音量增加" placement="left" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle class="tool-button" :disabled="!isPlaying" @click="sendLinuxKey(115)">
            <el-icon><CirclePlus /></el-icon>
          </el-button>
        </el-tooltip>
        <el-tooltip content="云机音量减小" placement="left" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle class="tool-button" :disabled="!isPlaying" @click="sendLinuxKey(114)">
            <el-icon><Minus /></el-icon>
          </el-button>
        </el-tooltip>
        <el-tooltip content="扩展云机画面" placement="left" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle class="tool-button" :class="{ active: isPresentationExpanded }" @click="toggleExpandedPresentation">
            <el-icon><FullScreen /></el-icon>
          </el-button>
        </el-tooltip>
        <el-tooltip content="显示虚拟导航键" placement="left" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle class="tool-button" :class="{ active: virtualKeysVisible }" @click="toggleVirtualKeys">
            <el-icon><Key /></el-icon>
          </el-button>
        </el-tooltip>
        <el-tooltip content="同步剪贴板到云机" placement="left" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle class="tool-button" :disabled="!isPlaying" @click="syncClipboard">
            <el-icon><DocumentCopy /></el-icon>
          </el-button>
        </el-tooltip>
        <el-tooltip content="上传文件到云机" placement="left" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle class="tool-button" @click="emit('upload-request')">
            <el-icon><UploadFilled /></el-icon>
          </el-button>
        </el-tooltip>
      </aside>

      <div v-if="status !== 'playing'" class="player-overlay">
        <el-icon v-if="status === 'opening'" class="loading-icon"><Loading /></el-icon>
        <el-icon v-else><Monitor /></el-icon>
        <p>{{ statusText }}</p>
        <el-button v-if="status === 'error'" type="primary" size="small" @click="reconnect">
          重新连接
        </el-button>
      </div>
    </div>

    <nav v-if="showControls && virtualKeysVisible" class="virtual-navigation" aria-label="云机虚拟导航键">
      <el-tooltip content="最近任务" placement="top" popper-class="player-tool-tooltip" :enterable="false">
        <el-button text circle class="navigation-button" :disabled="!isPlaying" @click="sendSystemCommand('recent')">
          <el-icon><Menu /></el-icon>
        </el-button>
      </el-tooltip>
      <el-tooltip content="云机主界面" placement="top" popper-class="player-tool-tooltip" :enterable="false">
        <el-button text circle class="navigation-button" :disabled="!isPlaying" @click="sendSystemCommand('home')">
          <el-icon><HomeFilled /></el-icon>
        </el-button>
      </el-tooltip>
      <el-tooltip content="云机返回" placement="top" popper-class="player-tool-tooltip" :enterable="false">
        <el-button text circle class="navigation-button" :disabled="!isPlaying" @click="sendSystemCommand('back')">
          <el-icon><Back /></el-icon>
        </el-button>
      </el-tooltip>
    </nav>

    <footer v-if="showControls" class="player-footer">
      <span>{{ statusText }}</span>
      <div class="player-actions">
        <el-tooltip content="重新建立媒体会话" placement="top" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle :disabled="status === 'opening'" @click="reconnect">
            <el-icon><RefreshRight /></el-icon>
          </el-button>
        </el-tooltip>
        <el-tooltip content="结束投屏" placement="top" popper-class="player-tool-tooltip" :enterable="false">
          <el-button text circle type="danger" :disabled="!routeId" @click="closeProjection">
            <el-icon><Close /></el-icon>
          </el-button>
        </el-tooltip>
      </div>
    </footer>
    <details v-if="diagnosticsEnabled" class="media-diagnostics">
      <summary>媒体诊断 · {{ diagnosticSummary.records || 0 }}</summary>
      <p>ICE：{{ diagnosticSummary.protocol || '待确认' }} / {{ diagnosticSummary.remoteProtocol || '待确认' }}</p>
      <p>缓冲：{{ diagnosticMs(diagnosticSummary.jitterBufferMs) }} · 解码：{{ diagnosticMs(diagnosticSummary.decodeMs) }}</p>
      <p>组装：{{ diagnosticMs(diagnosticSummary.assemblyMs) }}</p>
      <p>视频：{{ diagnosticSummary.width || '--' }} × {{ diagnosticSummary.height || '--' }}</p>
      <div class="diagnostic-actions">
        <el-button size="small" :icon="RefreshRight" @click="clearDiagnostics">新一轮记录</el-button>
        <el-button size="small" :icon="Download" @click="exportDiagnostics">导出诊断</el-button>
      </div>
    </details>
  </section>
</template>

<script setup>
/* global defineEmits, defineExpose, defineProps */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  ArrowRight,
  Back,
  CirclePlus,
  Close,
  DocumentCopy,
  Download,
  FullScreen,
  HomeFilled,
  Key,
  Loading,
  Menu,
  Minus,
  Monitor,
  RefreshRight,
  UploadFilled
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { createCloudPhoneSession } from '../../api/cloudDevice'
import { closeNativeSession, openNativeSession } from '../../api/nativeMedia'
import { loadVendorSdk } from '../../utils/loadVendorSdk'
import { installVendorAudioUnlock } from '../../utils/vendorAudioUnlock'
import { resizeVendorPlayer } from '../../utils/vendorPlayerLayout'
import { createMediaDiagnostics } from '../../utils/mediaDiagnostics'
import { resolveNativeWsProtocol, withNativeWsHost } from '../../utils/nativeMediaEndpoint'

const PREVIEW_PROFILE = Object.freeze({
  key: 'preview',
  label: '预览',
  bitrateKbps: 384,
  minFps: 3,
  maxFps: 3,
  width: 1080,
  height: 1920
})

// Full-control profiles request 1080P; the Android app is configured separately.
const QUALITY_PROFILES = Object.freeze([
  { key: 'smooth', label: '流畅', bitrateKbps: 384, minFps: 3, maxFps: 3 },
  { key: 'hd', label: '高清', bitrateKbps: 4096, minFps: 20, maxFps: 30 },
  { key: 'standard', label: '普通', bitrateKbps: 2500, minFps: 15, maxFps: 24 },
  { key: 'fast', label: '高速', bitrateKbps: 1500, minFps: 12, maxFps: 20 },
  { key: 'ultra-fast', label: '极速', bitrateKbps: 768, minFps: 8, maxFps: 15 }
].map((profile) => Object.freeze({ ...profile, width: 1080, height: 1920 })))

const props = defineProps({
  deviceId: {
    type: [Number, String],
    required: true
  },
  mode: {
    type: String,
    default: 'control'
  },
  showControls: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['status-change', 'route-opened', 'upload-request'])

const playerElement = ref(null)
const playerStage = ref(null)
const playerElementId = `native-bgs-player-${Math.random().toString(36).slice(2)}`
const status = ref('idle')
const statusText = ref('正在准备投屏')
const routeId = ref(null)
const remoteRotation = ref(0)
const selectedQuality = ref('hd')
const functionBarVisible = ref(true)
const virtualKeysVisible = ref(false)
const isPresentationExpanded = ref(false)
const mediaDelayMs = ref(null)
const isPreview = computed(() => props.mode === 'preview')
const diagnosticsEnabled = (process.env.NODE_ENV === 'development'
  || process.env.VUE_APP_MEDIA_DIAGNOSTICS === 'true') && props.mode !== 'preview'
  && new URLSearchParams(window.location.search).get('mediaDiagnostics') === '1'
const diagnosticSummary = ref({})
const isPlaying = computed(() => status.value === 'playing')
const qualityProfiles = QUALITY_PROFILES
const activeProfile = computed(() => {
  if (isPreview.value) {
    return PREVIEW_PROFILE
  }

  return qualityProfiles.find((profile) => profile.key === selectedQuality.value) || QUALITY_PROFILES[1]
})
const isLandscape = computed(() => !isPreview.value && (remoteRotation.value === 1 || remoteRotation.value === 3))
const delayText = computed(() => mediaDelayMs.value === null ? '延迟 --' : `延迟 ${mediaDelayMs.value}ms`)
const delayClass = computed(() => {
  if (mediaDelayMs.value === null) {
    return 'is-unavailable'
  }

  if (mediaDelayMs.value < 100) {
    return 'is-good'
  }

  return mediaDelayMs.value < 200 ? 'is-warning' : 'is-poor'
})

let activeRoute = null
let destroyed = false
let layoutObserver = null
let layoutFrame = null
let previewOrientationFrame = null
let lastPreviewLandscape = null
let sdkPlayerOwner = null
let backgroundReleasePromise = null
let backgroundResumePromise = null
let releasedForBackground = false
let backgroundEnteredAt = null
let projectionGeneration = 0
let pendingRouteRequest = null
let runtimeMetricsTimer = null
let disposeAudioUnlock = null
let mediaDiagnostics = null

function diagnosticMs(value) {
  return Number.isFinite(value) ? `${value.toFixed(1)}ms` : '--'
}

function clearDiagnostics() {
  mediaDiagnostics?.clear()
}

function exportDiagnostics() {
  if (!mediaDiagnostics) return
  const blob = new Blob([mediaDiagnostics.exportJsonl()], { type: 'application/x-ndjson' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `browser-media-${Date.now()}.jsonl`
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function updateStatus(nextStatus, text) {
  status.value = nextStatus
  statusText.value = text
  emit('status-change', { status: nextStatus, text })
}

function updateMediaDelay(sdk) {
  const directDelay = Number(sdk?.videoinfo?.netDelay)
  const fallbackDelay = Number(sdk?.webrtcRunInfo?.netDelay)
  const delay = Number.isFinite(directDelay) && directDelay > 0 ? directDelay : fallbackDelay

  if (Number.isFinite(delay) && delay > 0) {
    mediaDelayMs.value = Math.max(1, Math.round(delay))
  }
}

function startRuntimeMetrics() {
  stopRuntimeMetrics()
  updateMediaDelay(window.redfinger)
  runtimeMetricsTimer = window.setInterval(() => updateMediaDelay(window.redfinger), 1000)
}

function stopRuntimeMetrics() {
  if (runtimeMetricsTimer !== null) {
    window.clearInterval(runtimeMetricsTimer)
    runtimeMetricsTimer = null
  }
}

function releaseActiveSdkPlayer() {
  const sdk = window.redfinger

  if (sdk?.__cloudPhoneActivePlayer === sdkPlayerOwner) {
    sdk.__cloudPhoneActivePlayer = null
  }

  sdkPlayerOwner = null
}

function isCurrentProjection(generation) {
  return !destroyed && generation === projectionGeneration
}

async function releaseRouteQuietly(route) {
  if (!route?.routeId) {
    return
  }

  if (activeRoute?.routeId === route.routeId) {
    activeRoute = null
    routeId.value = null
  }

  try {
    await closeNativeSession(route.routeId)
  } catch {
    // The gateway may already have released the cancelled route.
  }
}

async function stopSdk() {
  mediaDiagnostics?.stop()
  stopRuntimeMetrics()
  disposeAudioUnlock?.()
  disposeAudioUnlock = null
  const sdk = window.redfinger

  if (!sdk) {
    return
  }

  try {
    if (typeof sdk.destroy === 'function') {
      await sdk.destroy()
    } else if (typeof sdk.destory === 'function') {
      await sdk.destory()
    } else {
      if (typeof sdk.stopObserve === 'function') {
        sdk.stopObserve()
      }

      if (typeof sdk.stopPlay === 'function') {
        sdk.stopPlay()
      }
    }
  } catch (error) {
    console.warn('停止 BgsSdk 播放失败', error)
  }

  if (playerElement.value) {
    playerElement.value.replaceChildren()
  }
}

function scheduleSdkLayout() {
  if (layoutFrame !== null) {
    return
  }

  layoutFrame = window.requestAnimationFrame(() => {
    layoutFrame = null
    const sdk = window.redfinger
    if (!destroyed && sdk?.__cloudPhoneActivePlayer === sdkPlayerOwner
      && resizeVendorPlayer(sdk, playerElement.value, {
        preview: isPreview.value,
        expanded: isPresentationExpanded.value
      })) {
      schedulePreviewOrientationReport(sdk)
    }
  })
}

function schedulePreviewOrientationReport(sdk) {
  if (!isPreview.value || previewOrientationFrame !== null) {
    return
  }

  previewOrientationFrame = window.requestAnimationFrame(() => {
    previewOrientationFrame = null
    const phoneElement = sdk?.phoneDom ? document.getElementById(sdk.phoneDom) : null
    const videoWidth = Number(sdk?.videoinfo?.width)
    const videoHeight = Number(sdk?.videoinfo?.height)

    if (!phoneElement) {
      return
    }

    if (playerElement.value) {
      playerElement.value.style.overflow = 'hidden'
      playerElement.value.style.overflowX = 'hidden'
      playerElement.value.style.overflowY = 'hidden'
    }

    const hasLandscapeVideo = Number.isFinite(videoWidth)
      && Number.isFinite(videoHeight)
      && videoWidth > videoHeight
    const phoneBounds = phoneElement.getBoundingClientRect()
    const hasLandscapePhoneNode = phoneBounds.width > phoneBounds.height
    const isLandscapePreview = hasLandscapeVideo || hasLandscapePhoneNode

    if (lastPreviewLandscape === isLandscapePreview) {
      return
    }

    lastPreviewLandscape = isLandscapePreview

    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'cloudphone-native-preview-orientation',
        deviceId: String(props.deviceId),
        landscape: isLandscapePreview
      }, window.location.origin)
    }
  })
}

function updateRemoteRotation(rotation) {
  const nextRotation = Number(rotation)

  if (Number.isInteger(nextRotation) && nextRotation >= 0 && nextRotation <= 3) {
    remoteRotation.value = nextRotation
  }
}

function startLayoutObserver() {
  if (layoutObserver || !playerStage.value || typeof ResizeObserver === 'undefined') {
    return
  }

  layoutObserver = new ResizeObserver(scheduleSdkLayout)
  layoutObserver.observe(playerElement.value)
}

function stopLayoutObserver() {
  if (layoutFrame !== null) {
    window.cancelAnimationFrame(layoutFrame)
    layoutFrame = null
  }

  if (previewOrientationFrame !== null) {
    window.cancelAnimationFrame(previewOrientationFrame)
    previewOrientationFrame = null
  }

  if (layoutObserver) {
    layoutObserver.disconnect()
    layoutObserver = null
  }
}

function releaseForBackground() {
  if (destroyed || !activeRoute) {
    return
  }

  releasedForBackground = true
  // Start releasing the gateway route before Safari has a chance to freeze JS.
  backgroundReleasePromise = closeProjection(true)
}

async function resumeAfterBackgroundRelease() {
  if (!releasedForBackground || destroyed || document.visibilityState !== 'visible') {
    return
  }

  const pendingRelease = backgroundReleasePromise
  backgroundReleasePromise = null

  if (pendingRelease) {
    await pendingRelease
  }

  if (destroyed || document.visibilityState !== 'visible') {
    return
  }

  releasedForBackground = false
  await startProjection('后台返回')
}

function markBackgroundEntered() {
  if (destroyed) {
    return
  }

  if (backgroundEnteredAt === null) {
    backgroundEnteredAt = Date.now()
  }

  releaseForBackground()
}

function resumeFromBackground() {
  if (destroyed) {
    return
  }

  backgroundEnteredAt = null

  // Fallback for browsers that resumed before their background close task ran.
  if (!releasedForBackground && activeRoute) {
    releaseForBackground()
  }

  if (backgroundResumePromise) {
    return
  }

  const resumePromise = resumeAfterBackgroundRelease()
  backgroundResumePromise = resumePromise
  void resumePromise.then(
    () => {
      if (backgroundResumePromise === resumePromise) {
        backgroundResumePromise = null
      }
    },
    (error) => {
      if (backgroundResumePromise === resumePromise) {
        backgroundResumePromise = null
      }
      console.warn('恢复后台媒体会话失败', error)
    }
  )
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    markBackgroundEntered()
    return
  }

  resumeFromBackground()
}

function handlePageHide() {
  markBackgroundEntered()
}

function installVendorControlCompatibility(sdk) {
  if (!sdk.__cloudPhoneControlHandlerInstalled && typeof sdk.conn_on_control === 'function') {
    const originalControlHandler = sdk.conn_on_control

    sdk.conn_on_control = function (packet, header) {
      if (!sdk.__cloudPhoneActivePlayer?.isAvailable()) {
        return Promise.resolve(0)
      }

      if (header?.subType === 111) {
        return Promise.resolve(0)
      }

      return originalControlHandler.call(this, packet, header)
    }

    sdk.__cloudPhoneControlHandlerInstalled = true
  }

  sdkPlayerOwner = {
    isAvailable: () => !destroyed && Boolean(playerElement.value?.isConnected)
  }
  sdk.__cloudPhoneActivePlayer = sdkPlayerOwner
}

function applyActiveProfile(sdk, requestQualityChange = false) {
  const profile = activeProfile.value
  const width = profile.width
  const height = profile.height
  const resolutionChanged = sdk.config.devWidth !== width || sdk.config.devHeight !== height

  sdk.config.devWidth = width
  sdk.config.devHeight = height
  sdk.config.isLiveVideo = isPreview.value
  sdk.config.isSendTouch = !isPreview.value
  sdk.config.isAudio = !isPreview.value
  sdk.config.isAudioPlay = !isPreview.value
  sdk.config.isMicrophoneLive = false
  sdk.config.isCameraLive = false
  sdk.config.isPcTouch = false
  sdk.config.keyboard = isPreview.value ? false : null
  sdk.config.bitrate = profile.bitrateKbps
  sdk.config.minFPS = profile.minFps
  sdk.config.maxFPS = profile.maxFps
  sdk.config.isWebRtc = process.env.VUE_APP_NATIVE_WEBRTC === 'true'
  mediaDiagnostics?.setProfile(profile)

  if (requestQualityChange && resolutionChanged && typeof sdk.setStreamConfig === 'function') {
    sdk.setStreamConfig(width, height, profile.bitrateKbps, profile.maxFps)
  } else if (requestQualityChange && typeof sdk.setupVideoQuality === 'function') {
    sdk.setupVideoQuality(-1, profile.bitrateKbps, profile.maxFps)
  }
}

function configureSdk(sdk) {
  installVendorControlCompatibility(sdk)
  disposeAudioUnlock = installVendorAudioUnlock(sdk, playerElement.value)

  sdk.onErrorMessage = (code) => {
    updateMediaDelay(sdk)
    if (!destroyed) {
      updateStatus('error', `播放错误：${code}`)
    }
  }

  sdk.onTimeout = (code) => {
    updateMediaDelay(sdk)
    if (!destroyed) {
      updateStatus('error', `连接超时：${code}`)
    }
  }

  sdk.onRunInformation = () => {
    updateMediaDelay(sdk)
  }

  sdk.onOpen = (code, result) => {
    if (destroyed) {
      return
    }

    if (result) {
      applyActiveProfile(sdk, true)
      scheduleSdkLayout()
      startRuntimeMetrics()
      updateStatus('playing', isPreview.value ? '预览已连接' : '投屏已连接')
      return
    }

    updateStatus('error', `媒体连接失败：${code}`)
  }

  sdk.config.devLevel = 0
  sdk.config.autoResize = false
  applyActiveProfile(sdk)
  sdk.onRenderedFirstFrame = scheduleSdkLayout
  sdk.onRotation = (event, rotation) => {
    updateRemoteRotation(rotation)
    scheduleSdkLayout()
  }
  sdk.onVideoInfoChange = scheduleSdkLayout
  sdk.onChangeResolution = scheduleSdkLayout
  sdk.setProtocol(resolveNativeWsProtocol({
    protocol: process.env.VUE_APP_NATIVE_WS_PROTOCOL,
    webRtc: process.env.VUE_APP_NATIVE_WEBRTC === 'true',
    pageProtocol: window.location.protocol
  }))
}

function selectQuality(key) {
  if (isPreview.value || key === selectedQuality.value) {
    return
  }

  selectedQuality.value = key
  const sdk = window.redfinger

  if (sdk && isPlaying.value) {
    try {
      applyActiveProfile(sdk, true)
      scheduleSdkLayout()
    } catch (error) {
      ElMessage.error(error.message || '切换清晰度失败')
    }
  }
}

function toggleFunctionBar() {
  functionBarVisible.value = !functionBarVisible.value
  void nextTick(scheduleSdkLayout)
}

function toggleExpandedPresentation() {
  isPresentationExpanded.value = !isPresentationExpanded.value
  void nextTick(scheduleSdkLayout)
}

function toggleVirtualKeys() {
  virtualKeysVisible.value = !virtualKeysVisible.value
  void nextTick(scheduleSdkLayout)
}

function sendSystemCommand(command) {
  const sdk = window.redfinger

  if (!sdk || !isPlaying.value || typeof sdk.sendCommand !== 'function') {
    return
  }

  const commandMap = {
    back: sdk.KEY_TYPE?.KEY_BACK,
    home: sdk.KEY_TYPE?.KEY_HOMEPAGE,
    recent: sdk.KEY_TYPE?.KEY_MENU
  }
  const keyCode = commandMap[command]

  if (typeof keyCode === 'number') {
    sdk.sendCommand(keyCode)
  }
}

function sendLinuxKey(keyCode) {
  const sdk = window.redfinger

  if (!sdk || !isPlaying.value || typeof sdk.sendKey !== 'function') {
    return
  }

  sdk.sendKey(keyCode, true)
  sdk.sendKey(keyCode, false)
}

async function syncClipboard() {
  const sdk = window.redfinger

  if (!sdk || !isPlaying.value || typeof sdk._sendInputClipper !== 'function') {
    ElMessage.warning('云机尚未连接')
    return
  }

  try {
    const text = await navigator.clipboard?.readText()
    if (!text) {
      ElMessage.warning('剪贴板为空，或浏览器没有提供读取权限')
      return
    }

    sdk._sendInputClipper(String(text), false)
    ElMessage.success('已同步到云机剪贴板')
  } catch (error) {
    ElMessage.warning('浏览器未授权读取剪贴板，请在 HTTPS 页面中允许权限后重试')
  }
}

async function requestFreshRoute() {
  const cloudPhoneSession = await createCloudPhoneSession(props.deviceId)
  const openedRoute = await openNativeSession(cloudPhoneSession.sessionTicket)

  if (!openedRoute?.routeId || !openedRoute?.connectToken) {
    throw new Error('媒体网关没有返回有效的投屏路由')
  }

  return openedRoute
}

async function startProjection(reason = '初始进入') {
  if (destroyed) {
    return
  }

  const generation = ++projectionGeneration

  releaseActiveSdkPlayer()
  await stopSdk()
  remoteRotation.value = 0
  mediaDelayMs.value = null
  lastPreviewLandscape = null
  updateStatus('opening', isPreview.value ? '正在创建预览会话' : '正在创建媒体会话')

  let routeRequest = null

  try {
    routeRequest = requestFreshRoute()
    pendingRouteRequest = routeRequest
    const route = await routeRequest

    if (pendingRouteRequest === routeRequest) {
      pendingRouteRequest = null
    }

    if (!isCurrentProjection(generation)) {
      await releaseRouteQuietly(route)
      return
    }

    activeRoute = route
    routeId.value = route.routeId
    emit('route-opened', { ...route, reason })

    await nextTick()
    const sdk = await loadVendorSdk()

    if (!isCurrentProjection(generation)) {
      await releaseRouteQuietly(route)
      return
    }

    if (!sdk) {
      throw new Error('BgsSdk 未加载成功')
    }

    configureSdk(sdk)
    if (diagnosticsEnabled) {
      mediaDiagnostics = createMediaDiagnostics({
        root: playerElement.value, sdk, profile: activeProfile.value,
        onSummary: (value) => { diagnosticSummary.value = value }
      })
    }
    updateStatus('opening', isPreview.value ? '正在连接预览媒体网关' : '正在连接媒体网关')
    const connectToken = withNativeWsHost(route.connectToken, process.env.VUE_APP_NATIVE_WS_HOST)
    sdk.play(playerElementId, sdk.VIDEO_BITRATE_HD, '', connectToken)
  } catch (error) {
    if (pendingRouteRequest === routeRequest) {
      pendingRouteRequest = null
    }

    if (!isCurrentProjection(generation)) {
      return
    }

    releaseActiveSdkPlayer()
    await stopSdk()
    activeRoute = null
    routeId.value = null
    updateStatus('error', error.message || (isPreview.value ? '原生预览启动失败' : '原生投屏启动失败'))
  }
}

async function closeProjection(releaseGatewayFirst = false) {
  const pendingRequest = pendingRouteRequest
  const currentRouteId = routeId.value
  projectionGeneration += 1

  releaseActiveSdkPlayer()
  const gatewayClosePromise = releaseGatewayFirst && currentRouteId
    ? closeNativeSession(currentRouteId)
    : null
  await stopSdk()
  activeRoute = null
  routeId.value = null

  try {
    if (gatewayClosePromise) {
      await gatewayClosePromise
    } else if (currentRouteId) {
      await closeNativeSession(currentRouteId)
    }

    if (pendingRequest) {
      const pendingRoute = await pendingRequest.catch(() => null)
      await releaseRouteQuietly(pendingRoute)
    }

    updateStatus('idle', '投屏已结束')
  } catch (error) {
    updateStatus('error', error.message || '关闭媒体会话失败')
  }
}

async function reconnect() {
  await closeProjection()
  await startProjection('手动重连')
}

onMounted(() => {
  startLayoutObserver()
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pagehide', handlePageHide)
  window.addEventListener('resize', scheduleSdkLayout)
  window.visualViewport?.addEventListener('resize', scheduleSdkLayout)
  void startProjection()
})

onBeforeUnmount(() => {
  destroyed = true
  stopLayoutObserver()
  stopRuntimeMetrics()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('pagehide', handlePageHide)
  window.removeEventListener('resize', scheduleSdkLayout)
  window.visualViewport?.removeEventListener('resize', scheduleSdkLayout)
  releaseActiveSdkPlayer()
  void closeProjection()
})

defineExpose({
  closeProjection,
  reconnect
})
</script>

<style scoped>
.media-diagnostics { position: fixed; z-index: 20; left: 8px; bottom: 8px; width: 238px; max-width: calc(100vw - 16px); padding: 8px; color: #e5e7eb; background: #202020; border: 1px solid #555; border-radius: 4px; font-size: 12px; }
.media-diagnostics summary { cursor: pointer; }
.media-diagnostics p { margin: 8px 0; overflow-wrap: anywhere; }
.diagnostic-actions { display: flex; flex-wrap: wrap; gap: 4px; }
.diagnostic-actions .el-button { margin: 0; }
.native-player { display: flex; flex-direction: column; width: 100%; height: 100%; min-width: 0; min-height: 0; }
.native-player.is-preview { width: 100%; height: 100%; }
.player-control-header { display: flex; flex-shrink: 0; align-items: center; justify-content: flex-end; gap: 8px; min-height: 42px; padding: 4px 0 6px; }
.function-bar-toggle, .quality-trigger { min-height: 32px; padding: 0 8px; color: #f4f8fa; font: inherit; font-size: 13px; background: transparent; border: 0; cursor: pointer; }
.function-bar-toggle.active { color: #35d07f; }
.quality-trigger { display: inline-flex; align-items: center; gap: 4px; }
.delay-chip { min-width: 76px; padding: 7px 8px; color: #9fa9ae; font-size: 12px; text-align: center; background: #171717; border-radius: 6px; }
.delay-chip.is-good { color: #35d07f; }
.delay-chip.is-warning { color: #e6b84d; }
.delay-chip.is-poor { color: #ed6969; }
.native-player.is-preview .player-stage { border-radius: 0; }
.player-stage { position: relative; flex: 1; width: 100%; min-height: 0; overflow: hidden; background: #090e13; border-radius: 8px; }
.player-surface { position: relative; width: 100%; height: 100%; overflow: hidden; touch-action: none; }
.player-surface.has-function-bar { width: calc(100% - 42px); }
.player-function-bar { position: absolute; top: 0; right: 0; bottom: 0; z-index: 2; display: flex; flex-direction: column; align-items: center; justify-content: space-evenly; width: 42px; padding: 6px 0; background: #151515; }
.tool-button, .navigation-button { flex-shrink: 0; width: 36px; height: 36px; margin: 0 !important; color: #ffffff; }
.tool-button.active { color: #35d07f; }
.player-overlay { position: absolute; inset: 0; z-index: 3; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #d7e3ea; background: rgba(9, 14, 19, 0.72); }
.player-overlay p { margin: 0; }
.loading-icon { animation: spin 1s linear infinite; }
.virtual-navigation { display: flex; flex-shrink: 0; align-items: center; justify-content: space-evenly; height: 44px; margin-top: 6px; background: #151515; border-radius: 6px; }
.player-footer { display: flex; flex-shrink: 0; align-items: center; justify-content: space-between; gap: 12px; min-height: 32px; margin-top: 4px; color: #8998a0; font-size: 12px; }
.player-footer > span { min-width: 0; overflow-wrap: anywhere; }
.player-actions { display: flex; gap: 2px; }
:global(.player-tool-tooltip) { pointer-events: none; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 640px), (max-height: 500px) {
  .native-player { width: 100%; }
  .player-control-header { justify-content: space-between; }
  .player-surface.has-function-bar { width: 100%; height: calc(100% - 44px); }
  .player-function-bar { top: auto; right: 0; bottom: 0; left: 0; flex-direction: row; justify-content: space-evenly; width: auto; height: 44px; padding: 4px; }
  .tool-button { width: 34px; height: 34px; }
}
</style>
